import type { FrontendInstallContext } from "@p0security/integrations-installer";
import {
  serviceNowAiConnectorName,
  serviceNowAiInstallSecretId,
  serviceNowAiTokenPrefix,
} from "@p0security/integrations-servicenow-ai-shared/secret-manager";
import type { ServiceNowAiComponents } from "@p0security/integrations-servicenow-ai-shared/types";
import {
  awsConnectorTf,
  gcpConnectorTf,
  p0InvokerEmail,
  p0InvokerRoleName,
  secretProviderFor,
  shellLines,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { ItemConfigOf } from "@p0security/shared/install/types";

type SecretManagerConfig = ItemConfigOf<
  ServiceNowAiComponents["instance"]
>["secretManager"];

type AwsSecretManagerConfig = Extract<SecretManagerConfig, { type: "aws-sm" }>;

type GcpSecretManagerConfig = Extract<SecretManagerConfig, { type: "gcp-sm" }>;

export type InstallContext = FrontendInstallContext<unknown>;

const GCP_CONNECTOR_IMAGE = {
  imageName: "docker.io/p0security/p0-connector-servicenow-ai-gcloud",
  digest:
    "sha-2fd68a0@sha256:935ecc126a3cc57eeb73081b9bde5ef7df99ed6e231cd71531a67f5609aefba7",
} as const;

const AWS_CONNECTOR_IMAGE = {
  imageName: "p0security/p0-connector-servicenow-ai-aws",
  digest:
    "sha-f9923eb@sha256:4b73e0d9b9b91ebaddbbb410d76464b8761cc46737a7786765f0ba9e97c7667b",
} as const;

const printInstallSecretJson = [
  "jq -n",
  '--arg provisionerClientSecret "$SN_PROVISIONER_CLIENT_SECRET"',
  '--arg jwtClientSecret "$SN_JWT_CLIENT_SECRET"',
  "--rawfile signingKey p0-servicenow-signing-key.pem",
  "'$ARGS.named'",
];

const awsInstallSecretId = (instance: string) =>
  secretProviderFor("aws").replacer(serviceNowAiInstallSecretId(instance));

export const awsStoreInstallSecret = (
  instance: string,
  secretManager: AwsSecretManagerConfig
) =>
  shellLines(
    ...printInstallSecretJson,
    "| aws secretsmanager put-secret-value",
    `--secret-id ${awsInstallSecretId(instance)}`,
    `--region ${secretManager.secretsRegion}`,
    "--secret-string file:///dev/stdin"
  );

export const awsTf = (
  context: InstallContext,
  instance: string,
  secretManager: AwsSecretManagerConfig
) =>
  awsConnectorTf({
    names: { resource: "servicenow", label: "ServiceNow" },
    image: AWS_CONNECTOR_IMAGE,
    accountId: secretManager.install,
    region: secretManager.connectorRegion,
    secretsRegion: secretManager.secretsRegion,
    connectorName: serviceNowAiConnectorName,
    p0RoleName: p0InvokerRoleName(context),
    tokenSecretPrefix: secretProviderFor("aws").replacer(
      serviceNowAiTokenPrefix(instance)
    ),
    installSecret: {
      resource: "install_secret",
      itemTfId: tfId(instance),
      secretId: awsInstallSecretId(instance),
      label: "install secret",
    },
  });

const gcpInstallSecretId = (instance: string) =>
  secretProviderFor("gcp").replacer(serviceNowAiInstallSecretId(instance));

/** Stores both client secrets and the signing key as the install secret's
 * JSON, from shell variables and a key file, so none of them is in the
 * command. */
export const gcpStoreInstallSecret = (
  instance: string,
  secretManager: GcpSecretManagerConfig
) =>
  shellLines(
    ...printInstallSecretJson,
    `| gcloud secrets versions add ${gcpInstallSecretId(instance)}`,
    `--project=${secretManager.install}`,
    "--data-file=-"
  );

export const gcpTf = (
  context: InstallContext,
  instance: string,
  secretManager: GcpSecretManagerConfig
) =>
  gcpConnectorTf({
    names: { resource: "servicenow", role: "ServiceNow", label: "ServiceNow" },
    image: GCP_CONNECTOR_IMAGE,
    projectId: secretManager.install,
    region: secretManager.connectorRegion,
    connectorName: secretManager.connectorServiceName,
    serviceAccountId:
      secretManager.connectorServiceAccount?.split("@")[0] ?? "",
    invokerEmail: p0InvokerEmail(context.installed),
    tokenSecretPrefix: secretProviderFor("gcp").replacer(
      serviceNowAiTokenPrefix(instance)
    ),
    installSecret: {
      resource: "install_secret",
      itemTfId: tfId(instance),
      secretId: gcpInstallSecretId(instance),
      label: "install secret",
    },
  });
